import PropTypes from "prop-types";
import { memo, useEffect, useMemo, useState } from "react";
import Paragraphs from "app/pages/.shared/Paragraphs";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import { ProductsCarousel } from "app/pages/.shared/ProductsCarousel";
import { FormattedMessage } from "react-intl";
import IconRight from "app/pages/.shared/static/icons/IconRight";
import ProductCard from "app/pages/.shared/ProductCard/ProductCard";
import ProductList from "app/pages/Listing/ProductList/ProductList";
import ProductListBlockFilter from "app/pages/.shared/DynamicBlocks/blocks/ProductListBlock/ProductListBlockFilter";
import classNames from "classnames";
import "./ProductListBlock.scss";
import Button, {
	BUTTON_ICON_ALIGNMENTS,
	BUTTON_SIZES,
	BUTTON_VARIANTS,
} from "app/pages/.shared/form/Button";
import ProductFilters from "app/features/filters/productFilters/ProductFilters";
import { useFacets } from "app/features/filters/productFilters/hooks/useFacets";
import { useIntl } from "react-intl";
import { PRODUCTS_FILTERS_KEY } from "app/constants";
import { FILTER_SELECTION_TYPES } from "app/features/filters/productFilters/constants/filterSelectionTypes";

const ProductListBlock = ({
	title,
	shortVersion,
	shouldHideProductPriceBanner,
	products = [],
	redirectUrl,
	displayFilters = false,
	isMobile,
}) => {
	const { formatDate } = useIntl();

	const [productsFiltered, setProductsFiltered] = useState(products);

	// sans ce useEffect, productsFiltered est tout le temps vide
	useEffect(() => {
		setProductsFiltered(products);
	}, [products]);

	const facetConfigs = useMemo(
		() => [
			{
				field: PRODUCTS_FILTERS_KEY.DESTINATION,
				labelKey: "product.page.filter.destinations.label",
				getter: item => item.country,
				selectionMode: FILTER_SELECTION_TYPES.SINGLE,
			},
			{
				field: PRODUCTS_FILTERS_KEY.TOPIC,
				labelKey: "listing.filter.theme.label",
				getter: item => item.topics,
				selectionMode: FILTER_SELECTION_TYPES.SINGLE,
			},
			{
				field: PRODUCTS_FILTERS_KEY.MONTH,
				labelKey: "listing.filter.month.label",
				getter: item => item.months.map(month => month.label),
				formatLabel: label => {
					const [year, month] = label.split("/");
					const date = new Date(Number(year), Number(month) - 1, 1);
					return formatDate(date, { month: "long", year: "numeric" });
				},
				selectionMode: FILTER_SELECTION_TYPES.SINGLE,
			},
		],
		[]
	);

	const { filteredProducts, facets } = useFacets(products, facetConfigs);

	const productListBLockTitleClassNames = classNames({
		"product-list-block__title": true,
		"product-list-block__title--with-filter": displayFilters,
	});

	if (products.length === 0) {
		return false;
	}

	let productsNode = false;

	if (isMobile) {
		if (shortVersion) {
			productsNode = (
				<ProductsCarousel
					products={filteredProducts}
					hidePriceBanner={shouldHideProductPriceBanner}
					noSwiping={!isMobile}
				/>
			);
		} else {
			productsNode = (
				<ProductList
					products={filteredProducts}
					hidePriceBanner={shouldHideProductPriceBanner}
					hideMembershipBlock={true}
				/>
			);
		}
	} else if (shortVersion) {
		productsNode = (
			<div className="product-list-block__products">
				{productsFiltered.map((product, index) => {
					return (
						<div key={product.id} data-testid={`product-${product.uri}`}>
							<ProductCard
								product={product}
								position={index}
								hidePriceBanner={shouldHideProductPriceBanner}
							/>
						</div>
					);
				})}
			</div>
		);
	} else {
		productsNode = (
			<ProductList
				products={productsFiltered}
				hidePriceBanner={shouldHideProductPriceBanner}
				hideMembershipBlock={true}
			/>
		);
	}

	return (
		<div className="product-list-block" data-testid="product-list-block">
			<div className="product-list-block__container">
				<div className={productListBLockTitleClassNames}>
					{title && (
						<Typography
							variant={TYPOGRAPHY_VARIANTS.XL3}
							variantMD={TYPOGRAPHY_VARIANTS.XL4}
							isBold
							component="h2"
						>
							<Paragraphs paragraphs={title} enableHTMLFormatting />
						</Typography>
					)}

					{!isMobile && shortVersion && (
						<div className="product-list-block__view-all-offers-link">
							<Button
								variant={BUTTON_VARIANTS.BORDERLESS}
								size={BUTTON_SIZES.LARGE}
								to={redirectUrl}
								label={
									<FormattedMessage id="products.carousel.view.all.offers.label" />
								}
								alignIcon={BUTTON_ICON_ALIGNMENTS.RIGHT}
								icon={<IconRight />}
							/>
						</div>
					)}
				</div>
			</div>

			{displayFilters &&
				!shortVersion &&
				(isMobile ? (
					<ProductFilters facets={facets} resultsCount={filteredProducts.length} />
				) : (
					<ProductListBlockFilter
						products={products}
						filterProduct={setProductsFiltered}
					/>
				))}

			<div className="product-list-block__container">
				{productsNode}

				{isMobile && shortVersion && (
					<div className="product-list-block__view-all-offers-button">
						<Button
							variant={BUTTON_VARIANTS.STROKE}
							size={BUTTON_SIZES.LARGE}
							to={redirectUrl}
							label={
								<FormattedMessage id="products.carousel.view.all.offers.label" />
							}
							alignIcon={BUTTON_ICON_ALIGNMENTS.RIGHT}
							icon={<IconRight />}
						/>
					</div>
				)}
			</div>
		</div>
	);
};

ProductListBlock.propTypes = {
	products: PropTypes.array,
	title: PropTypes.string,
	redirectUrl: PropTypes.string,
	shouldHideProductPriceBanner: PropTypes.bool,
	shortVersion: PropTypes.bool,
	displayFilters: PropTypes.bool,
	isMobile: PropTypes.bool,
};

export default memo(ProductListBlock);
